import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { castDescribe, castIsOfficial, ADDRESS_RE, ISSUER_BEACON, NVDA, VERIFIER } from '../lib/chain';
import { errorText, fmtUsd, fmtUtc } from '../lib/format';
import type { Snapshot } from '../lib/snapshot';
import { checkToken, readNvda, type CheckResult } from '../lib/verifier';
import { AddrLink } from './AddrLink';
import { CopyButton } from './CopyButton';
import { DescribeRows } from './DescribeRows';
import { Stamp } from './Stamp';

const USDG_EXAMPLE = '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168';

function Cast({ cmd, label }: { cmd: string; label: string }) {
  return <pre className="cast"><code>{cmd}</code><CopyButton text={cmd} label={label} /></pre>;
}

/** describe(NVDA) on the mainnet verifier, read when the page opens and every minute after. */
export function NvdaCard() {
  const q = useQuery({ queryKey: ['nvda'], queryFn: readNvda, refetchInterval: 60000, retry: false, refetchOnWindowFocus: false });
  let body: ReactNode;
  if (q.data) {
    body = (
      <>
        <dl className="live-grid">
          <dt>token</dt><dd><AddrLink addr={NVDA} /> <CopyButton text={NVDA} label="NVDA address" /></dd>
          <DescribeRows d={q.data} />
        </dl>
        <Cast cmd={castDescribe(NVDA)} label="cast command" />
        <Stamp />
      </>
    );
  } else if (q.error) {
    body = <p className="live-note">describe(NVDA) on the mainnet verifier could not be read: {errorText(q.error)}. The page keeps trying every minute.</p>;
  } else {
    body = <p className="live-note">Waiting for the mainnet RPC (needs JavaScript).</p>;
  }
  return <div id="nvda-live">{body}</div>;
}

const VerifierLink = () => <AddrLink addr={VERIFIER} />;

type Outcome =
  | { state: 'idle' }
  | { state: 'asking'; addr: string }
  | { state: 'done'; addr: string; result: CheckResult }
  | { state: 'failed'; addr: string; error: string };

function resultClass(o: Outcome): string {
  if (o.state === 'failed') return 'result unknown';
  if (o.state !== 'done') return 'result';
  if (o.result.kind === 'no-code') return 'result no';
  return o.result.official ? 'result ok' : 'result no';
}

function Verdict({ addr, r }: { addr: string; r: Extract<CheckResult, { kind: 'verdict' }> }) {
  return (
    <>
      <h4>{r.official ? 'Official Stock Token' : 'Not an official Stock Token'}</h4>
      <p>This is the answer of the verifier contract at <VerifierLink />: isOfficial(<AddrLink addr={addr} />) returned {r.official ? 'true' : 'false'}.</p>
      <dl>
        {r.describe ? <DescribeRows d={r.describe} /> : <><dt>describe()</dt><dd>reverted or could not be decoded for this address</dd></>}
        {r.known && (
          <><dt>Chainlink feed</dt><dd><AddrLink addr={r.known.feed} /> ({r.known.sym}, from our monitor's list{r.known.reading ? `, last moved ${fmtUtc(r.known.reading.updatedAt)} at ${fmtUsd(r.known.reading.price, 2)} USD` : ''})</dd></>
        )}
        <dt>Beacon slot</dt><dd>{r.beacon ? <><AddrLink addr={r.beacon} />{r.beacon.toLowerCase() === ISSUER_BEACON ? " (the issuer's beacon)" : ''}</> : r.slot ? 'empty (not a beacon proxy)' : 'could not be read'}</dd>
        <dt>Issuer beacon</dt><dd><AddrLink addr={ISSUER_BEACON} /></dd>
        {r.impl && <><dt>Implementation</dt><dd><AddrLink addr={r.impl} />{r.implIsIssuer ? " (the issuer's Stock contract)" : ''}</dd></>}
      </dl>
      <p>{r.official
        ? "The verifier found the issuer's proxy code at this address, a uid from which the issuer's factory derives exactly this address, and a symbol. The beacon and implementation lines above are the same evidence, read directly from the token for you to compare."
        : `The verifier requires four facts and could not establish all of them: the issuer's proxy code at this address, a non-zero uid, a factory address derived from that uid that equals this address, and a non-empty symbol. ${r.missing.length ? 'From the evidence above: ' + r.missing.join(', ') + '.' : 'The evidence above does not show which one failed.'} A token fails this check when it is not the issuer's contract, and also when it is a factory-deployed token that was never initialised; the verifier does not distinguish the two.`}</p>
      <p className="repro">Reproduce with Foundry:</p>
      <Cast cmd={castIsOfficial(addr)} label="cast isOfficial command" />
      <Cast cmd={castDescribe(addr)} label="cast describe command" />
    </>
  );
}

function ResultBody({ o }: { o: Outcome }) {
  if (o.state === 'idle') return null;
  if (o.state === 'asking') return <p>Asking the verifier contract about {o.addr}.</p>;
  if (o.state === 'failed') {
    return (
      <>
        <h4>Could not check</h4>
        <p>The verifier's answer could not be read: {o.error}. This is not a verdict about the token. Try again in a moment, or run the call yourself:</p>
        <Cast cmd={castIsOfficial(o.addr)} label="cast isOfficial command" />
      </>
    );
  }
  if (o.result.kind === 'no-code') {
    return (
      <>
        <h4>No contract at this address</h4>
        <p>The address has no code on Robinhood Chain mainnet (eth_getCode returned empty). It cannot be a Stock Token, and the verifier at <VerifierLink /> answers false for it.</p>
      </>
    );
  }
  return <Verdict addr={o.addr} r={o.result} />;
}

export function Check({ snapshot }: { snapshot: Snapshot | null }) {
  const [addr, setAddr] = useState('');
  const [outcome, setOutcome] = useState<Outcome>({ state: 'idle' });

  const run = (a: string) => {
    setOutcome({ state: 'asking', addr: a });
    checkToken(a, snapshot)
      .then((result) => { setOutcome({ state: 'done', addr: a, result }); })
      .catch((e: unknown) => { setOutcome({ state: 'failed', addr: a, error: errorText(e) }); });
  };
  const onSubmit = (ev: SyntheticEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const v = addr.trim();
    if (ADDRESS_RE.test(v)) run(v);
  };
  const example = (a: string) => { setAddr(a); run(a); };

  return (
    <section className="section" id="check">
      <h2>Check a token</h2>
      <div className="livebox" aria-live="polite">
        <h4>Worked example: describe(NVDA) on the mainnet verifier</h4>
        <p className="live-intro">The verifier's describe() for the official NVDA token, read from Robinhood Chain mainnet when you open this page and every minute after. The cast line below reproduces the call from a shell.</p>
        <NvdaCard />
      </div>
      <p className="table-intro">Paste a token address on Robinhood Chain. The page calls isOfficial and describe on the verifier contract at <a href="https://robinhoodchain.blockscout.com/address/0x574cB84717b8aF7Df5F40a8344D783728317114f" rel="noopener">0x574cB847…17114f</a>, through this site's RPC relay or a public endpoint, and shows the contract's answer with the token's beacon slot and implementation as evidence. Nothing is sent anywhere but the RPC. A throttled node that answers with empty data is reported as "could not check", never as a verdict.</p>
      <form className="checker" id="checker" onSubmit={onSubmit}>
        <label htmlFor="addr" className="sr-only">Token address</label>
        <input id="addr" name="addr" type="text" inputMode="text" autoComplete="off" spellCheck="false" placeholder="0x…" pattern="^0x[0-9a-fA-F]{40}$" required value={addr} onChange={(e) => { setAddr(e.target.value); }} />
        <button type="submit" className="button">Check</button>
      </form>
      <p className="examples">Try <button type="button" className="linkish" data-addr={NVDA} onClick={() => { example(NVDA); }}>the official NVDA token</button> or <button type="button" className="linkish" data-addr={USDG_EXAMPLE} onClick={() => { example(USDG_EXAMPLE); }}>USDG, which is not a Stock Token</button>.</p>
      <div className={resultClass(outcome)} id="result" aria-live="polite" hidden={outcome.state === 'idle'}><ResultBody o={outcome} /></div>
      <p className="fineprint">"Official" means the contract matches the issuer's contracts on chain. It does not mean the token is endorsed, free of risk, or suitable for you.</p>
    </section>
  );
}
